import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, HStack, IconButton, Input, Spinner, Text, VStack } from "@chakra-ui/react";
import { RoomLocationWithReading } from "@tether-web-portal/types/RoomLocation";
import { Check, X } from "lucide-react";
import { SecondaryButton } from "@tether-web-portal/components/button/SecondaryButton";

enum SubmitStatus {
  INACTIVE = "inactive",
  EDITING = "editing",
  LOADING = "loading",
  SUCCESS = "success",
}

const ChildLocationFormItem = ({
  id,
  name,
  subtitle,
  onChangeLocation,
}: {
  id: string;
  name: string;
  subtitle: string;
  onChangeLocation: (id: string, input: Partial<RoomLocationWithReading>) => Promise<void>;
}) => {
  const [revertableName, setRevertableName] = useState(name);
  const [formName, setFormName] = useState(name);

  const [isFocused, setIsFocused] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>(SubmitStatus.INACTIVE);

  const successClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onInputValueChange = (value: string) => {
    setFormName(value);
    setStatus(SubmitStatus.EDITING);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (formName === revertableName) return;
    setStatus(SubmitStatus.LOADING);

    try {
      await onChangeLocation(id, { name: formName });
      setRevertableName(formName);
      setStatus(SubmitStatus.SUCCESS);

      successClearRef.current = setTimeout(() => {
        setStatus((s) => (s === SubmitStatus.SUCCESS ? SubmitStatus.INACTIVE : s));
      }, 3000);
    } catch {
      setStatus(SubmitStatus.INACTIVE);
    }
  };

  useEffect(() => {
    return function unmountTimeoutCleanup() {
      if (!successClearRef.current) return;
      clearTimeout(successClearRef.current);
    };
  }, []);

  const handleCancel = () => {
    setFormName(revertableName);
    setStatus(SubmitStatus.INACTIVE);
  };

  return (
    <Flex direction="column" gap={2} width="100%">
      <form onSubmit={handleSubmit}>
        <HStack spacing={2} width="100%" align="center">
          <Input
            flex={1}
            minW={0}
            value={formName}
            onChange={(e) => onInputValueChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
          {(isFocused || status !== SubmitStatus.INACTIVE) && (
            <>
              <SecondaryButton
                title="Save"
                type="submit"
                size="sm"
                isDisabled={status !== SubmitStatus.EDITING}
              />
              <Box width="32px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                {status === SubmitStatus.LOADING && <Spinner size="sm" color="gray.500" />}
                {status === SubmitStatus.SUCCESS && (
                  <Check size={18} strokeWidth={2.5} color="var(--chakra-colors-green-400)" />
                )}
                {(status === SubmitStatus.EDITING || status === SubmitStatus.INACTIVE) && (
                  <IconButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Cancel editing"
                    isDisabled={status !== SubmitStatus.EDITING}
                    icon={<X size={18} />}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={handleCancel}
                  />
                )}
              </Box>
            </>
          )}
        </HStack>
      </form>

      <Text fontSize="sm" color="gray.500">
        {subtitle}
      </Text>
    </Flex>
  );
};

export const ChildLocationForm = ({
  title,
  subtitle,
  indexLabel,
  childLocations,
  onChangeLocation,
}: {
  title: string;
  subtitle: string;
  indexLabel: string;
  childLocations?: RoomLocationWithReading[];
  onChangeLocation: (id: string, input: Partial<RoomLocationWithReading>) => Promise<void>;
}) => {
  const items = useMemo(() => {
    return [...(childLocations ?? [])]
      .map((child) => ({
        name: child.name,
        id: child.id,
        subDeviceIndex: child.device?.subDeviceIndex ?? 0,
      }))
      .sort((a, b) => a.subDeviceIndex - b.subDeviceIndex);
  }, [childLocations]);

  return (
    <VStack gap={4} width="100%" alignItems="flex-start">
      <Flex direction="column" gap={1} alignItems="flex-start">
        <Text fontSize="xl" fontWeight="bold">
          {title}
        </Text>
        <Text fontSize="sm" color="gray.500">
          {subtitle}
        </Text>
      </Flex>

      {items.map(({ id, name }, index) => (
        <ChildLocationFormItem
          key={id}
          id={id}
          name={name}
          subtitle={`${indexLabel} ${index + 1}`}
          onChangeLocation={onChangeLocation}
        />
      ))}
    </VStack>
  );
};
